import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { BookOpen, Clock, Video, FileText, ExternalLink, MessageSquare, Calendar } from 'lucide-react';
import { Lesson, LessonFormat, LessonCardProps } from '@/types/ai-devquest';
import { commentsService } from '@/services/commentsService';

/**
 * Component for displaying a lesson card
 */
export const LessonCard: React.FC<LessonCardProps> = ({
  lesson,
  isCompleted = false,
  onClick
}) => {
  // Format estimated time
  const formatTime = (minutes: number): string => {
    if (minutes < 60) {
      return `${minutes} min`;
    }
    
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    
    if (remainingMinutes === 0) {
      return `${hours} hr`;
    }
    
    return `${hours} hr ${remainingMinutes} min`;
  };
  
  // Get and format the lesson date (using updatedAt or createdAt)
  const getLessonDate = (lesson: Lesson): string => {
    // Try updatedAt first, then createdAt
    const timestamp = lesson.updatedAt || lesson.createdAt;
    
    if (!timestamp) {
      return 'No date';
    }
    
    let date;
    if (timestamp.toDate) {
      // Firebase Timestamp
      date = timestamp.toDate();
    } else if (timestamp instanceof Date) {
      date = timestamp;
    } else {
      return 'No date';
    }
    
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };
  
  const [commentCount, setCommentCount] = useState(0);

  useEffect(() => {
    const fetchCommentCount = async () => {
      try {
        const count = await commentsService.getLessonCommentCount(lesson.id);
        setCommentCount(count);
      } catch (err) {
        console.error('Error fetching comment count:', err);
      }
    };
    
    fetchCommentCount();
  }, [lesson.id]);

  return (
    <Card 
      className={`h-full cursor-pointer transition-colors bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 ${
        isCompleted ? 'bg-gray-100 dark:bg-gray-600' : ''
      }`}
      onClick={onClick}
    >
      <CardContent className="p-4 h-full flex flex-col">
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-start flex-grow mr-2">
            {/* Lesson Icon - placeholder for now */}
            <div className="h-8 w-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center mr-2 flex-shrink-0">
              <BookOpen className="h-4 w-4 text-gray-600 dark:text-gray-400" />
            </div>
            <h4 className="font-semibold text-gray-900 dark:text-white break-words">{lesson.title}</h4>
          </div>
          
          {/* Completion Status and Comments */}
          <div className="flex items-center gap-2">
            {commentCount > 0 && (
              <div className="flex items-center gap-1 bg-primary/10 text-primary text-xs px-2 py-1 rounded">
                <MessageSquare className="h-3 w-3" />
                {commentCount}
              </div>
            )}
            {isCompleted && (
              <div className="bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 text-xs px-2 py-1 rounded">
                Completed
              </div>
            )}
          </div>
        </div>
        
        {/* Lesson Description */}
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2 flex-grow whitespace-pre-wrap">
          {lesson.description}
        </p>
        
        {/* Lesson Metadata */}
        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mt-2 flex-wrap">
          <div className="flex items-center space-x-3">
            <div className="flex items-center">
  
            <Clock className="h-3 w-3 mr-1" />
              {formatTime(lesson.estimatedTime)}
            </div>
            <div className="flex items-center">
              <Calendar className="h-3 w-3 mr-1" />
              {getLessonDate(lesson)}
            </div>
          </div>
          
          {/* Format Icons */}
          <div className="flex space-x-2">
            {lesson.formats.includes(LessonFormat.VIDEO) && (
              <div title="Video" className="flex items-center">
                <Video className="h-3 w-3" />
              </div>
            )}
            {lesson.formats.includes(LessonFormat.DOCUMENT) && (
              <div title="Document" className="flex items-center">
                <FileText className="h-3 w-3" />
              </div>
            )}
            {lesson.formats.includes(LessonFormat.LINK) && (
              <div title="External Link" className="flex items-center">
                <ExternalLink className="h-3 w-3" />
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};